import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '../../../context/ThemeContext';
import { ROLE_FULL_NAMES } from '../../../utils/constants';
import { getInitialsFromName, stringToColor } from '../../../utils/helpers';
import { usePermissions } from '../../../hooks/usePermissions';
import { Icons } from '../Icons';
import './Header.css';

interface HeaderProps {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
}

export function Header({ sidebarOpen, onToggleSidebar }: HeaderProps) {
  const { user, userRole, logout } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Close dropdown when route changes
  useEffect(() => {
    setShowUserMenu(false);
  }, [location.pathname]);

  // Handle keyboard navigation in dropdown
  useEffect(() => {
    if (!showUserMenu) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const canAccessSettings = hasPermission('settings.manage');
      const menuItems = canAccessSettings ? ['settings', 'logout'] : ['logout'];
      const maxIndex = menuItems.length - 1;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex(prev => (prev < maxIndex ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex(prev => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex === -1) return;
          if (menuItems[focusedIndex] === 'settings') {
            setShowUserMenu(false);
            navigate('/settings');
          } else if (menuItems[focusedIndex] === 'logout') {
            handleLogout();
          }
          break;
        case 'Escape':
          e.preventDefault();
          setShowUserMenu(false);
          setFocusedIndex(-1);
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showUserMenu, focusedIndex, hasPermission, navigate]);

  // Reset focused index when menu opens/closes
  useEffect(() => {
    if (showUserMenu) {
      setFocusedIndex(0);
    } else {
      setFocusedIndex(-1);
    }
  }, [showUserMenu]);

  if (!user || !userRole) return null;

  const canAccessSettings = hasPermission('settings.manage');

  const handleLogout = async () => {
    setShowUserMenu(false);
    await logout();
    navigate('/login');
  };

  return (
    <header className="dashboard-header">
      <div className="header-left">
        <button className="mobile-menu-btn" onClick={onToggleSidebar}>
          <Icons.Menu />
        </button>
      </div>

      <div className="header-right">
        <button
          className="header-btn theme-toggle"
          onClick={toggleTheme}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? <Icons.Sun /> : <Icons.Moon />}
        </button>

        <div className="user-menu-wrapper">
          <button
            className="user-menu-trigger"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <div
              className="user-avatar-header"
              style={{ backgroundColor: stringToColor(user.email) }}
            >
              {getInitialsFromName(user.name)}
            </div>
            <div className="user-info-header">
              <span className="user-name-header">
                {user.name}
              </span>
              <span className="user-role-header">
                {ROLE_FULL_NAMES[userRole]}
              </span>
            </div>
            <Icons.ChevronDown className="chevron-icon" />
          </button>

          {showUserMenu && (
            <div className="user-dropdown">
              {canAccessSettings && (
                <div 
                  className={`dropdown-item ${focusedIndex === 0 ? 'focused' : ''}`}
                  onClick={() => { setShowUserMenu(false); navigate('/settings'); }}
                >
                  <Icons.Settings />
                  <span>Settings</span>
                </div>
              )}
              <div 
                className={`dropdown-item logout ${focusedIndex === (canAccessSettings ? 1 : 0) ? 'focused' : ''}`}
                onClick={handleLogout}
              >
                <Icons.Logout />
                <span>Logout</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close dropdown */}
      {showUserMenu && (
        <div
          className="dropdown-overlay"
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </header>
  );
}

export default Header;
